import { addYears } from 'date-fns';
import React from 'react';
import DatePicker from 'react-datepicker';
import { Controller, useFormContext } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { GroupBase } from 'react-select';
import { UserHasPermission } from 'utils/auth/roles-and-permissions';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import {
  AddEditCustomOperation,
  AddPropertyField,
} from '@/modules/settings/property';
import CustomOperationHeader from '@/modules/settings/property/components/Jobs/AddEditCustomOperation/Header';
import AddEditTeamMember from '@/modules/settings/Team/components/AddEditTeamMember';
import {
  useGetFieldsList,
  useGetJobsList,
  useGetSupervisorsList,
} from '@/modules/workingDay/apiHooks';
import { useCreateTechnicalOperationUtils } from '@/modules/workingDay/components/CreateTechnicalOperation/logic';
import { IWorkingDay } from '@/modules/workingDay/types';
import {
  CircularBgImage,
  ErrorMessage,
  InputLabel,
  SelectField,
  Typography,
} from '@/shared/components/atoms';
import InfiniteSelectField from '@/shared/components/atoms/SelectField/InfiniteSelectField';
import TooltipLite from '@/shared/components/atoms/TooltipLite';
import { permissions } from '@/shared/constant/roles-and-permissions';
import { useAuth } from '@/shared/hooks/context/auth';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { useDidUpdateEffect } from '@/shared/hooks/useDidUpdateEffect';
import { IGeneral } from '@/shared/types';

// TODO: refactor file and create seperate component for each input
const OperationCategory = () => {
  const { user } = useAuth();
  const { openModal, closeModal } = useModal();
  const {
    t,
    i18n: { language },
  } = useTranslation('common');
  const [selectedAsset] = useStore((store) => store?.asset);

  const {
    control,
    watch,
    resetField,
    formState: { errors },
  } = useFormContext<IWorkingDay.IOperationFormValuesSchema>();

  const getAssigneeName = (obj: IGeneral.NameIdPair) =>
    `${obj.name} ${obj?.id === user?.id ? `(${t('you')})` : ''}`;

  const { getJobsListOptions } = useCreateTechnicalOperationUtils();

  const {
    data: supervisors,
    isLoading: isSupervisorsLoading,
    fetchNextPage: supervisorFetchNextPage,
    refetch: refetchSupervisors,
  } = useGetSupervisorsList(selectedAsset?.id);
  const supervisorsList = supervisors?.pages.flatMap((page) => page.items);

  const {
    data: fields,
    isLoading: isFieldsLoading,
    fetchNextPage: fieldsFetchNextPage,
    refetch: refetchFields,
  } = useGetFieldsList(selectedAsset?.id);
  const fieldsList = fields?.pages.flatMap((page) => page.items);

  const fieldWatcher = watch('field.cropId');
  const { data: jobsList, refetch } = useGetJobsList({
    entityId: selectedAsset?.id,
    cropId: fieldWatcher,
  });

  const handleCreateCustomOperation = () => {
    const field = watch('field');
    openModal(
      <AddEditCustomOperation
        cropId={field.cropId}
        onSuccessFunction={refetch}
      />,
      {
        title: (
          <CustomOperationHeader
            cropName={field.cropName}
            cropImage={field.image}
            type='operation'
          />
        ),
        containerClassName: '!overflow-y-visible min-w-[360px]',
        onClose(closeModal) {
          refetch();
          closeModal();
        },
      }
    );
  };

  const getOperationOption = (operation: IWorkingDay.IJobSchecma) => (
    <div className='flex items-center justify-between'>
      {operation.jobName}
      {operation.suppliers && (
        <TooltipLite
          padding='0'
          content={
            <div>
              <Typography
                className='rounded-t-2xl px-24 py-16 odd:bg-white even:bg-neutralVariant-99'
                variant='caption'
              >
                {t('crews-with-contracts')}
              </Typography>
              {operation.suppliers.map((supplier) => (
                <Typography
                  className='px-24 py-16 last:rounded-b-2xl odd:bg-white even:bg-neutralVariant-99'
                  weight='semiBold'
                  variant='body2'
                  key={supplier.id}
                >
                  {supplier.name}
                </Typography>
              ))}
            </div>
          }
        >
          <Typography
            variant='caption'
            weight='semiBold'
            className='rounded-lg bg-outline-default px-8 py-4 text-primary-default'
          >
            {t('contract-exists')}
          </Typography>
        </TooltipLite>
      )}
    </div>
  );

  const handleFieldAdded = () => {
    toast.success(t('field-added-sucessfully'));
    refetchFields();
  };

  const hasPermissionToAddField = UserHasPermission({
    action: permissions.CAN_ADD_FIELD_IN_TECHNICAL_OPERATION,
    role: user?.userGroupId,
  });

  useDidUpdateEffect(() => {
    resetField('job', { defaultValue: undefined });
  }, [fieldWatcher]);

  const handleTeamMemberAddedSuccess = () => {
    closeModal();
    toast.success(t(`warehouse-team-member-added`));
    refetchSupervisors();
  };

  const hasPermissionToAddTeamMember = UserHasPermission({
    action: permissions.CAN_ADD_TEAM_MEMBER_TO_WAREHOUSE,
    role: user?.userGroupId,
  });

  return (
    <div className='flex flex-col gap-16'>
      <InfiniteSelectField
        id='assignee'
        errorMessageId='assignee.id'
        getLabel={getAssigneeName}
        getValue={(supervisor: IGeneral.NameIdPair) => ({
          name: getAssigneeName(supervisor),
          id: supervisor.id,
        })}
        value={supervisorsList?.filter(
          (user) => user.id === watch('assignee')?.id
        )}
        isDisabled={isSupervisorsLoading}
        borderColor={themeColors().outline.variant}
        label={t('assignee')}
        placeholder={t('assignee')}
        list={supervisorsList ?? []}
        createOption={
          hasPermissionToAddTeamMember
            ? {
                title: t('add-team-member-to-warehouse'),
                subTitle: t('add-team-member-to-operation-sub-title'),
                onClick: () => {
                  openModal(
                    <AddEditTeamMember
                      onSuccess={handleTeamMemberAddedSuccess}
                      formName='create technical operation - add team member from select field'
                    />,
                    {
                      variant: 'full',
                      title: t('add-team-member-to-warehouse'),
                    }
                  );
                },
              }
            : undefined
        }
        py='0.5rem'
        px='0.5rem'
        borderRadius='1rem'
        width='w-full min-w-0'
        fetchNextPage={supervisorFetchNextPage}
      />
      <InfiniteSelectField
        id='field'
        errorMessageId='field.id'
        getLabel={(field: IWorkingDay.IFieldSchecma) => field.name}
        formatOptionLabel={(option: IWorkingDay.IFieldSchecma) => (
          <div className='flex items-center gap-1'>
            {option.image && <CircularBgImage imageSrc={option.image} />}
            {option.name}
          </div>
        )}
        getValue={(field: IWorkingDay.IFieldSchecma) => field}
        value={fieldsList?.find((field) => field.id === watch('field')?.id)}
        createOption={
          hasPermissionToAddField
            ? {
                title: t('add-field'),
                subTitle: t('add-field-subtitle'),
                onClick: () =>
                  openModal(
                    <AddPropertyField
                      selectedAsset={selectedAsset}
                      onSuccess={handleFieldAdded}
                    />,
                    {
                      variant: 'full',
                      title: (
                        <Typography variant='h2'>{t('add-field')}</Typography>
                      ),
                    }
                  ),
              }
            : undefined
        }
        isDisabled={isFieldsLoading}
        borderColor={themeColors().outline.variant}
        label={t('Field')}
        placeholder={t('Field')}
        list={fieldsList}
        py='0.5rem'
        px='0.5rem'
        borderRadius='1rem'
        width='w-full min-w-0'
        fetchNextPage={fieldsFetchNextPage}
      />
      <SelectField<
        IWorkingDay.IJobSchecma,
        true,
        GroupBase<IWorkingDay.IJobSchecma>
      >
        id='job'
        errorMessageId='job.jobId'
        isSearchable
        isGroup
        borderColor={themeColors().grey[200]}
        width='w-full'
        py='0.375rem'
        borderRadius='1rem'
        label={t('Jobs')}
        maxMenuHeight={200}
        hideSelectedOptions={false}
        placeholder={t('Jobs')}
        getOptionLabel={getJobsListOptions(jobsList)}
        options={getJobsListOptions(jobsList)}
        list={jobsList ?? []}
        isDisabled={!watch('field')}
        getLabel={(item: IWorkingDay.IJobSchecma) => item.jobName}
        getValue={(item: IWorkingDay.IJobSchecma) => item}
        value={
          jobsList
            ? [...jobsList.customJobs, ...jobsList.defaultJobs].filter(
                (job) => job.jobId === watch('job')?.jobId
              )
            : undefined
        }
        createOption={
          fieldWatcher
            ? {
                title: t('create-custom-operation'),
                onClick: handleCreateCustomOperation,
              }
            : undefined
        }
        customOption={(option) => getOperationOption(option.data)}
      />
      <div className='flex w-full flex-col'>
        <InputLabel
          label={t('planned-date')}
          id='startDate'
          className='capitalize'
        />
        <Controller
          name='startDate'
          control={control}
          render={({ field: { onChange } }) => (
            <DatePicker
              wrapperClassName='relative'
              locale={language}
              selected={watch('startDate')}
              className='w-full cursor-pointer rounded-xl border-xs border-outline-variant bg-background-transparent px-12 py-14 capitalize'
              placeholderText={t('planned-date')}
              onChange={(date) => {
                if (date) onChange(date);
              }}
              maxDate={addYears(new Date(), 1)}
            />
          )}
        />
        {errors?.startDate && (
          <ErrorMessage errorMessage={errors.startDate.message ?? ''} />
        )}
      </div>
    </div>
  );
};
export { OperationCategory };
